import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { 
  Plus,
  Target,
  TrendingUp,
  Euro,
  Users,
  Calendar,
  Pencil,
  Trophy
} from 'lucide-react';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';

const GOAL_TYPES = [
  { id: 'vendas', label: 'Vendas', icon: Euro, unit: 'EUR' },
  { id: 'oportunidades', label: 'Oportunidades', icon: TrendingUp, unit: 'unidades' },
  { id: 'contactos', label: 'Contactos', icon: Users, unit: 'unidades' },
  { id: 'chamadas', label: 'Chamadas', icon: Target, unit: 'unidades' },
  { id: 'visitas', label: 'Visitas', icon: Calendar, unit: 'unidades' }
];

const PERIODS = [
  { id: 'semanal', label: 'Semanal' },
  { id: 'mensal', label: 'Mensal' },
  { id: 'trimestral', label: 'Trimestral' },
  { id: 'anual', label: 'Anual' }
];

export default function GoalsPage() {
  const { api } = useAuth();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  
  const [form, setForm] = useState({
    title: '',
    goal_type: 'vendas',
    target_value: '',
    current_value: '0',
    unit: 'EUR',
    period: 'mensal',
    start_date: format(new Date(), 'yyyy-MM-dd'),
    end_date: '',
    team_goal: false
  });

  useEffect(() => {
    loadGoals();
  }, []);

  const loadGoals = async () => {
    try {
      const response = await api().get('/goals');
      setGoals(response.data);
    } catch (error) {
      toast.error('Erro ao carregar objetivos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const data = {
      ...form,
      target_value: parseFloat(form.target_value) || 0,
      current_value: parseFloat(form.current_value) || 0
    };
    
    try {
      if (editingGoal) {
        await api().put(`/goals/${editingGoal.id}`, data);
        toast.success('Objetivo atualizado!');
      } else {
        await api().post('/goals', data);
        toast.success('Objetivo criado!');
      }
      
      setShowModal(false);
      resetForm();
      loadGoals();
    } catch (error) {
      toast.error('Erro ao guardar objetivo');
    }
  };

  const handleUpdateProgress = async (goalId, newValue) => {
    try {
      await api().patch(`/goals/${goalId}/progress`, { current_value: parseFloat(newValue) });
      toast.success('Progresso atualizado!');
      loadGoals();
    } catch (error) {
      toast.error('Erro ao atualizar progresso');
    }
  };

  const handleEdit = (goal) => {
    setEditingGoal(goal);
    setForm({
      title: goal.title || '',
      goal_type: goal.goal_type || 'vendas',
      target_value: goal.target_value?.toString() || '',
      current_value: goal.current_value?.toString() || '0',
      unit: goal.unit || 'EUR',
      period: goal.period || 'mensal',
      start_date: goal.start_date || format(new Date(), 'yyyy-MM-dd'),
      end_date: goal.end_date || '',
      team_goal: goal.team_goal || false
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setForm({
      title: '',
      goal_type: 'vendas',
      target_value: '',
      current_value: '0',
      unit: 'EUR',
      period: 'mensal',
      start_date: format(new Date(), 'yyyy-MM-dd'),
      end_date: '',
      team_goal: false
    });
    setEditingGoal(null);
  };

  const formatValue = (value, unit) => {
    if (unit === 'EUR') {
      return new Intl.NumberFormat('pt-PT', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0
      }).format(value || 0);
    }
    return `${value || 0} ${unit}`;
  };

  const getProgress = (current, target) => {
    if (!target) return 0;
    return Math.min((current / target) * 100, 100);
  };

  const getProgressColor = (progress) => {
    if (progress >= 100) return 'bg-emerald-500';
    if (progress >= 75) return 'bg-blue-500';
    if (progress >= 50) return 'bg-amber-500';
    return 'bg-red-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-800" />
      </div>
    );
  }

  const teamGoals = goals.filter(g => g.team_goal);
  const individualGoals = goals.filter(g => !g.team_goal);

  return (
    <div className="space-y-6" data-testid="goals-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">
            {goals.length} objetivos definidos
          </p>
        </div>
        <Button 
          className="bg-indigo-800 hover:bg-indigo-900"
          onClick={() => { resetForm(); setShowModal(true); }}
          data-testid="new-goal-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Novo Objetivo
        </Button>
      </div>

      {/* Team Goals */}
      {teamGoals.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold font-[Manrope] mb-4 flex items-center gap-2">
            <Users className="h-5 w-5 text-indigo-600" />
            Objetivos da Equipa
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {teamGoals.map((goal) => {
              const progress = getProgress(goal.current_value, goal.target_value);
              const goalType = GOAL_TYPES.find(t => t.id === goal.goal_type);
              
              return (
                <Card 
                  key={goal.id} 
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => handleEdit(goal)}
                  data-testid={`goal-card-${goal.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-indigo-100 rounded-lg">
                          {goalType && <goalType.icon className="h-5 w-5 text-indigo-600" />}
                        </div>
                        <div>
                          <h4 className="font-medium">{goal.title}</h4>
                          <Badge variant="outline" className="text-xs mt-1">
                            {PERIODS.find(p => p.id === goal.period)?.label}
                          </Badge>
                        </div>
                      </div>
                      {progress >= 100 && (
                        <Trophy className="h-5 w-5 text-amber-500" />
                      )}
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Progresso</span>
                        <span className="font-medium">{Math.round(progress)}%</span>
                      </div>
                      <Progress value={progress} className={`h-2 ${getProgressColor(progress)}`} />
                      <div className="flex justify-between text-sm tabular-nums">
                        <span>{formatValue(goal.current_value, goal.unit)}</span>
                        <span className="text-muted-foreground">/ {formatValue(goal.target_value, goal.unit)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Individual Goals */}
      <div>
        <h3 className="text-lg font-semibold font-[Manrope] mb-4 flex items-center gap-2">
          <Target className="h-5 w-5 text-indigo-600" />
          Objetivos Individuais
        </h3>
        
        {individualGoals.length === 0 ? (
          <Card>
            <CardContent className="p-16 text-center">
              <Target className="h-16 w-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold font-[Manrope]">Nenhum objetivo individual</h3>
              <p className="text-muted-foreground mt-1">Defina metas para acompanhar o seu progresso</p>
              <Button 
                className="mt-4 bg-indigo-800 hover:bg-indigo-900"
                onClick={() => { resetForm(); setShowModal(true); }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Criar Objetivo
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {individualGoals.map((goal) => {
              const progress = getProgress(goal.current_value, goal.target_value);
              const goalType = GOAL_TYPES.find(t => t.id === goal.goal_type);
              
              return (
                <Card 
                  key={goal.id} 
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => handleEdit(goal)}
                  data-testid={`goal-card-${goal.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-emerald-100 rounded-lg">
                          {goalType && <goalType.icon className="h-5 w-5 text-emerald-600" />}
                        </div>
                        <div>
                          <h4 className="font-medium">{goal.title}</h4>
                          <Badge variant="outline" className="text-xs mt-1">
                            {PERIODS.find(p => p.id === goal.period)?.label}
                          </Badge>
                        </div>
                      </div>
                      {progress >= 100 && (
                        <Trophy className="h-5 w-5 text-amber-500" />
                      )}
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Progresso</span>
                        <span className="font-medium">{Math.round(progress)}%</span>
                      </div>
                      <Progress value={progress} className={`h-2`} />
                      <div className="flex justify-between text-sm tabular-nums">
                        <span>{formatValue(goal.current_value, goal.unit)}</span>
                        <span className="text-muted-foreground">/ {formatValue(goal.target_value, goal.unit)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={(open) => { if (!open) resetForm(); setShowModal(open); }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">
              {editingGoal ? 'Editar Objetivo' : 'Novo Objetivo'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="title">Título *</Label>
                <Input
                  id="title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Ex: Vendas Q1 2024"
                  required
                  data-testid="goal-title-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo</Label>
                  <Select 
                    value={form.goal_type} 
                    onValueChange={(v) => {
                      const goalType = GOAL_TYPES.find(t => t.id === v);
                      setForm({ ...form, goal_type: v, unit: goalType?.unit || 'EUR' });
                    }}
                  >
                    <SelectTrigger data-testid="goal-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {GOAL_TYPES.map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Período</Label>
                  <Select value={form.period} onValueChange={(v) => setForm({ ...form, period: v })}>
                    <SelectTrigger data-testid="goal-period-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PERIODS.map((period) => (
                        <SelectItem key={period.id} value={period.id}>
                          {period.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="target_value">Meta</Label>
                  <Input
                    id="target_value"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.target_value}
                    onChange={(e) => setForm({ ...form, target_value: e.target.value })}
                    required
                    data-testid="goal-target-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="current_value">Valor Atual</Label>
                  <Input
                    id="current_value"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.current_value}
                    onChange={(e) => setForm({ ...form, current_value: e.target.value })}
                    data-testid="goal-current-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_date">Data Início</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                    required
                    data-testid="goal-start-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_date">Data Fim</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={form.end_date}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                    required
                    data-testid="goal-end-input"
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label>Objetivo da Equipa</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Visível para todos os membros
                  </p>
                </div>
                <Switch
                  checked={form.team_goal}
                  onCheckedChange={(checked) => setForm({ ...form, team_goal: checked })}
                  data-testid="goal-team-switch"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-indigo-800 hover:bg-indigo-900" data-testid="save-goal-btn">
                {editingGoal ? 'Guardar' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
